import type {
  CreateEquipmentInput,
  EquipmentDetail,
  EquipmentFormOptions,
  EquipmentImportPreview,
  EquipmentImportResult,
  EquipmentImportRow,
  EquipmentStatus,
  EquipmentSummary,
  EquipmentType,
  UpdateEquipmentInput,
} from "@crewledger/shared/schemas";
import { apiFetch, downloadAuthenticatedFile } from "@/lib/api";

export async function fetchEquipment(params?: {
  page?: number;
  pageSize?: number;
  siteId?: string;
  type?: string;
  status?: string;
  q?: string;
}) {
  const search = new URLSearchParams();
  if (params?.page) search.set("page", String(params.page));
  if (params?.pageSize) search.set("pageSize", String(params.pageSize));
  if (params?.siteId) search.set("siteId", params.siteId);
  if (params?.type) search.set("type", params.type);
  if (params?.status) search.set("status", params.status);
  if (params?.q) search.set("q", params.q);

  const qs = search.toString();
  return apiFetch<{ data: EquipmentSummary[]; meta: { total?: number } }>(
    `/api/equipment${qs ? `?${qs}` : ""}`,
  );
}

export async function fetchEquipmentOptions() {
  return apiFetch<{ data: EquipmentFormOptions }>("/api/equipment/options");
}

export async function fetchEquipmentItem(id: string) {
  return apiFetch<{ data: EquipmentDetail }>(`/api/equipment/${id}`);
}

export async function createEquipment(input: CreateEquipmentInput) {
  return apiFetch<{ data: EquipmentDetail }>("/api/equipment", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateEquipment(id: string, input: UpdateEquipmentInput) {
  return apiFetch<{ data: EquipmentDetail }>(`/api/equipment/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export async function updateEquipmentStatus(
  id: string,
  status: EquipmentStatus,
  notes?: string | null,
) {
  return apiFetch<{ data: EquipmentDetail }>(`/api/equipment/${id}/status`, {
    method: "POST",
    body: JSON.stringify({ status, notes }),
  });
}

export const EQUIPMENT_TYPE_LABELS: Record<EquipmentType, string> = {
  HEAVY_EQUIPMENT: "Heavy Equipment",
  VEHICLE: "Vehicle",
  TOOL: "Tool",
  RENTAL: "Rental",
  OTHER: "Other",
};

export const EQUIPMENT_STATUS_LABELS: Record<EquipmentStatus, string> = {
  AVAILABLE: "Available",
  IN_USE: "In Use",
  MAINTENANCE: "Maintenance",
  OUT_OF_SERVICE: "Out of Service",
};

export async function downloadEquipmentImportTemplate(): Promise<void> {
  await downloadAuthenticatedFile("/api/templates/equipment.csv", "equipment.csv");
}

export async function previewEquipmentImport(csv: string) {
  return apiFetch<{ data: EquipmentImportPreview }>("/api/equipment/import/preview", {
    method: "POST",
    headers: { "Content-Type": "text/csv" },
    body: csv,
  });
}

export async function confirmEquipmentImport(rows: EquipmentImportRow[]) {
  return apiFetch<{ data: EquipmentImportResult }>("/api/equipment/import", {
    method: "POST",
    body: JSON.stringify({ rows }),
  });
}
